import { memo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
// Import Types
import type { Language } from 'types';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { selectUserId } from 'store/slices/userData';
import { selectHeaderLanguages, setHeaderLanguagesList } from 'store/slices/header';
// Import UI Components
import { Option, Icon } from '@geneui/components';
// Import Components
import UserProfileSettingsModal from './UserProfileSettingsModal';
import UserInfoModal from 'components/UserInfoModal';
// Import Hooks
import { useGTMEvent } from 'hooks/UseGTM';
// Import Constants
import { UserProfilePopoverViews } from './constants';
import { LanguagesFlagsPaths } from 'constants/utils';
import { l, GACategoryEvents } from 'constants/common';
// Import Styles
import 'assets/scss/settingsPopup.scss';

const { GAUserSettingsCategory } = GACategoryEvents;

const UserProfile = ({
    signOutHandler,
    changeLanguageHandler,
    saveSettings,
    settingsModalVisibleState,
    setSettingsModalVisibleState,
    isVisibleProfileModal,
    setIsVisibleProfileModal,
}) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { mainView, languageView } = UserProfilePopoverViews;

    const languages = useAppSelector(selectHeaderLanguages);
    const UserId = useAppSelector(selectUserId);
    const { logEvent } = useGTMEvent();

    const [selectedLanguage, setSelectedLanguage] = useState<Nullable<Language>>(null);
    const [userProfilePopoverView, setUserProfilePopoverView] = useState(mainView);
    // const [settingsModalVisibleState, setSettingsModalVisibleState] = useState(false);

    const findSelectedLanguage = () => {
        const language = languages.filter((lng) => lng.IsSelected === true);
        if (language.length) {
            setSelectedLanguage(language[0]);
        }
    };

    const handleSave = (newSettings, isOnlyModeChanged) => {
        return saveSettings({ ...newSettings }, isOnlyModeChanged);
    };

    const selectLanguage = (langId) => {
        const languagesList = languages.map((language) => ({
            ...language,
            IsSelected: language.Id === langId,
        }));

        logEvent(
            GAUserSettingsCategory.name,
            GAUserSettingsCategory.events.changeLanguage,
            languages.find((language) => language.Id === langId)?.DisplayName ?? String(langId),
        );

        changeLanguageHandler(langId);
        dispatch(setHeaderLanguagesList(languagesList));
        setUserProfilePopoverView(mainView);
    };

    const closeProfileModalHandler = () => {
        setIsVisibleProfileModal(false);
    };

    const mainViewConfig = [
        {
            title: t(l.Profile),
            onClick: () => {
                setIsVisibleProfileModal(!isVisibleProfileModal);
            },
        },
        {
            title: t(l.Settings),
            onClick: () => {
                setSettingsModalVisibleState(!settingsModalVisibleState);
            },
        },
        {
            title: `${t(l.Language)} : ${selectedLanguage?.DisplayName}`,
            onClick: () => setUserProfilePopoverView(languageView),
        },
        {
            title: t(l.LogOut),
            onClick: signOutHandler,
        },
    ];

    useEffect(findSelectedLanguage, [languages]);

    const mainViewContent = mainViewConfig.map(({ title, onClick }, index) => {
        const rightContent = index === 1 ? <Icon type="bc-icon-arrow-right" /> : null;

        return <Option key={title} title={title} onClick={onClick} rightCustomElement={rightContent} border="bottom" />;
    });

    const languageViewContent = languages.map(({ DisplayName, Id, IsSelected }) => {
        const leftContent = IsSelected ? <Icon className="bc-icon-check" /> : null;

        return (
            <Option
                key={Id}
                title={DisplayName}
                active={IsSelected}
                onClick={() => selectLanguage(Id)}
                leftCustomElement={<img className="crm-lng-img" src={LanguagesFlagsPaths[Id]} alt={DisplayName} />}
                rightCustomElement={leftContent}
            />
        );
    });

    return (
        <>
            <div className="crm-user-profile-cnt">
                {userProfilePopoverView === languageView && (
                    <Option
                        title={t(l.ChooseLanguage)}
                        border="bottom"
                        onClick={() => setUserProfilePopoverView(mainView)}
                        leftCustomElement={<Icon type="bc-icon-arrow-left" />}
                    />
                )}

                {userProfilePopoverView === mainView ? mainViewContent : languageViewContent}
            </div>
            {settingsModalVisibleState && (
                <UserProfileSettingsModal closeModal={setSettingsModalVisibleState} saveHandler={handleSave} />
            )}
            {isVisibleProfileModal && (
                <UserInfoModal
                    openedModalData={{ UserId }}
                    isVisibleModal={isVisibleProfileModal}
                    closeModalHandler={closeProfileModalHandler}
                    withPortal={false}
                />
            )}
        </>
    );
};

UserProfile.propTypes = {
    signOutHandler: PropTypes.func.isRequired,
    changeLanguageHandler: PropTypes.func.isRequired,
    saveSettings: PropTypes.func.isRequired,
    settingsModalVisibleState: PropTypes.bool.isRequired,
    setSettingsModalVisibleState: PropTypes.func.isRequired,
    isVisibleProfileModal: PropTypes.bool.isRequired,
    setIsVisibleProfileModal: PropTypes.func.isRequired,
};

export default memo(UserProfile);
